// Suggestion-stats panel rendered above the indexed posts list in "Suggestion"
// mode. The activity card was retired from this surface in Phase 6 — see
// `docs/planning/org_activity_top_level_mode.md`. Org activity now lives in
// its own primary mode.

import type { AdsSuggestionStatsResponse } from "@/lib/adsAssistantClient";

export function AdsAssistantEngagementBars(props: {
  stats: AdsSuggestionStatsResponse | null;
  loadSuggestionStats: boolean;
  statsScope: "org" | "location";
  setStatsScope: (s: "org" | "location") => void;
  engagementError: string | null;
}) {
  const { stats, loadSuggestionStats, statsScope, setStatsScope, engagementError } = props;

  if (!engagementError && !(loadSuggestionStats && stats)) return null;

  return (
    <div className="space-y-3">
      {engagementError ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
          {engagementError}
        </p>
      ) : null}

      {loadSuggestionStats && stats ? (
        <div className="rounded-xl border border-violet-200/80 bg-violet-50/40 px-3 py-3 dark:border-violet-900/50 dark:bg-violet-950/20 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-semibold text-violet-950 dark:text-violet-100">Suggestion insights</span>
            <div className="flex rounded-lg border border-violet-200/90 bg-white p-0.5 text-[11px] dark:border-violet-800 dark:bg-zinc-900">
              <button
                type="button"
                className={
                  "rounded-md px-2 py-1 " +
                  (statsScope === "org"
                    ? "bg-violet-600 text-white"
                    : "text-violet-900 dark:text-violet-200 hover:bg-violet-100/80 dark:hover:bg-violet-900/40")
                }
                onClick={() => setStatsScope("org")}
              >
                Org
              </button>
              <button
                type="button"
                className={
                  "rounded-md px-2 py-1 " +
                  (statsScope === "location"
                    ? "bg-violet-600 text-white"
                    : "text-violet-900 dark:text-violet-200 hover:bg-violet-100/80 dark:hover:bg-violet-900/40")
                }
                onClick={() => setStatsScope("location")}
              >
                My location
              </button>
            </div>
          </div>
          <p className="text-[11px] text-violet-900/85 dark:text-violet-200/85">
            You: <strong>{stats.my_total_posts}</strong> posts · Org ({statsScope}): <strong>{stats.org_total_posts}</strong>{" "}
            posts in retention window.
          </p>
          {stats.notice ? (
            <p className="text-[11px] text-amber-900 dark:text-amber-200/90">{stats.notice}</p>
          ) : null}
          {stats.gap_suggestions.length ? (
            <div className="text-[11px] text-violet-950 dark:text-violet-100 space-y-1">
              <span className="font-semibold">Try a popular template you haven&apos;t used:</span>
              <ul className="list-disc pl-4">
                {stats.gap_suggestions.map((t) => (
                  <li key={t.template_id}>
                    <strong>{t.name}</strong> — ~{t.org_share_pct}% of org logs; you have {t.my_count}.
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {stats.fresh_templates.length ? (
            <div className="text-[11px] text-violet-900/90 dark:text-violet-200/90">
              <span className="font-semibold">Recently active formats: </span>
              {stats.fresh_templates.map((t) => t.name).join(", ")}
            </div>
          ) : null}
          {(stats.variety_suggestions ?? []).length ? (
            <div className="rounded-lg border border-violet-200/60 bg-white/70 px-2 py-2 text-[11px] text-violet-950 dark:border-violet-800/60 dark:bg-zinc-900/50 dark:text-violet-100 space-y-1">
              <span className="font-semibold">Mix it up: </span>
              <span className="text-violet-900/90 dark:text-violet-200/85">
                Your last several logs used the same template a lot — try a different format the org also uses.
              </span>
              <ul className="list-disc pl-4">
                {(stats.variety_suggestions ?? []).map((t) => (
                  <li key={t.template_id}>
                    <strong>{t.name}</strong> — worth an experiment this week.
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
